import { Icon, Label, mergeStyles, Stack, TooltipHost } from "@fluentui/react";
import { styles } from "../ExperimentDetailsForm/experimentDetailsForm.styles";
import { useMemo } from "react";
import { useId } from "@fluentui/react-hooks";
import sumBy from "lodash/sumBy";

interface FormTabLabelProps {
  /**
   * The display text for the tab label
   */
  label: string;
  /**
   * Array of field names applicable to the form section. Used to lookup
   * applicable errors.
   */
  fields: string[];
  /**
   * Number of unsaved changes in this form section. Used to show change indicator/counts on tabs..
   */
  changesCount: number;
  /**
   * Validation errors keyed by field name. Used to show error indicator/counts on tabs.
   */
  errors?: Record<string, string[]>;
  /**
   * When true, applies selected tab styling
   */
  isSelected?: boolean;
  /**
   * When true, hides the specific change count in tooltip (shows generic message,
   * e.g. "form has changes" instead of "form has 3 changes").
   */
  hideChangeTotal?: boolean;
}

/**
 * Smart tab label component that displays the tab name along with visual indicators
 * for validation errors and unsaved changes. Provides tooltips which will show the
 * counts for errors or changes.
 */
const FormTabLabel: React.FC<FormTabLabelProps> = ({
  label,
  errors = {},
  fields,
  isSelected,
  changesCount,
  hideChangeTotal = false,
}) => {
  const errorTooltipId = useId(label + "-error-tooltip");
  const changeTooltipId = useId(label + "-change-tooltip");
  const errorFieldCounts = useMemo(
    () => fields.map((field) => errors[field]?.length).filter(Boolean),
    [errors, fields],
  );
  const errorCount = useMemo(() => sumBy(errorFieldCounts), [errorFieldCounts]);
  const hasErrors = errorCount > 0;
  const hasChanged = changesCount > 0;

  return (
    <Stack horizontal className={styles.experimentDetailsFormTabLabelContainer}>
      {hasErrors && (
        <TooltipHost
          content={
            hasErrors
              ? `${label} form has ${errorCount} error${
                  errorCount > 1 ? "s" : ""
                }`
              : undefined
          }
          id={errorTooltipId}
        >
          <Icon
            iconName="Error"
            className={styles.experimentDetailsFormTabLabelIconError}
            aria-label="Error"
            data-testid="error-icon"
          />
        </TooltipHost>
      )}
      <Stack horizontal verticalAlign="start">
        <Label
          className={mergeStyles(
            styles.experimentDetailsFormTabLabel,
            isSelected && styles.experimentDetailsFormTabLabelSelected,
            hasErrors && styles.experimentDetailsFormTabLabelError,
          )}
        >
          {label}
        </Label>
        {hasChanged && (
          <TooltipHost
            content={
              hasChanged
                ? hideChangeTotal
                  ? `${label} form has changed`
                  : `${label} form has ${changesCount} change${
                      changesCount > 1 ? "s" : ""
                    }`
                : undefined
            }
            id={changeTooltipId}
          >
            <Icon
              iconName="StatusCircleInner"
              className={styles.experimentDetailsFormTabLabelIconChanged}
              aria-label="Change"
              data-testid="change-icon"
            />
          </TooltipHost>
        )}
      </Stack>
    </Stack>
  );
};

export default FormTabLabel;
